import { useState } from 'react';
import clsx from 'clsx';
import { Icon } from '../../../layout/Icon.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { formatIQD } from '../../../shared/format.js';
import { api, apiError } from '../../../shared/api.js';
import { toast } from '../../../shared/useToast.js';
import { ProductSearchBox } from './ProductSearchBox.jsx';

function marginColor(margin) {
  if (margin < 20) return 'danger';
  if (margin < 50) return 'warning';
  return 'success';
}

// نفس معادلة الخادم (purchases/helpers/invoiceState.js):
//   الإجمالي قبل الخصم = الكمية × سعر الشراء للوحدة
//   قيمة الخصم = نسبة من ذلك الإجمالي، أو مبلغ ثابت
//   الإجمالي بعد الخصم = الفرق، والكلفة الصافية للوحدة = الإجمالي بعد الخصم ÷ الكمية
// الهامش يُحسب على الكلفة الصافية لأنها ما دُفع فعلاً للوحدة.
export function lineMath(it) {
  const qty = Math.max(0, Math.round(Number(it.qty) || 0));
  const cost = Math.max(0, Math.round(Number(it.cost) || 0));
  const sale = Math.max(0, Math.round(Number(it.salePrice) || 0));
  const grossTotal = qty * cost;
  const type = it.discountType === 'percent' ? 'percent' : 'amount';
  const raw = Math.max(0, Number(it.discount) || 0);
  let discountValue = type === 'percent'
    ? Math.round((grossTotal * Math.min(100, raw)) / 100)
    : Math.round(raw);
  if (discountValue > grossTotal) discountValue = grossTotal;
  const total = Math.max(0, grossTotal - discountValue);
  const netUnitCost = qty > 0 ? Math.round(total / qty) : 0;
  const margin = netUnitCost > 0 ? Math.round(((sale - netUnitCost) / netUnitCost) * 100) : 0;
  const invalid = type === 'percent' ? raw > 100 : Math.round(raw) > grossTotal;
  return { qty, cost, sale, grossTotal, discountValue, total, netUnitCost, margin, invalid, type };
}

export function ItemsGrid({ items, onChange, warehouseId, readOnly }) {
  const [scanInput, setScanInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  function setField(idx, field, val) {
    const next = items.map((it, i) => i === idx ? { ...it, [field]: val } : it);
    onChange(next);
  }

  function removeRow(idx) {
    onChange(items.filter((_, i) => i !== idx));
  }

  function addRow() {
    onChange([
      ...items,
      {
        _key: Date.now() + Math.random(),
        productId: '', name: '', barcode: '', qty: 1, cost: 0, salePrice: 0,
        discount: 0, discountType: 'amount'
      }
    ]);
  }

  // فكّ ربط الصف بمنتجه ليُعاد البحث داخله — الكمية والكلفة وسعر البيع تبقى
  // كما كتبها المستخدم.
  function unlinkRow(idx) {
    onChange(items.map((it, i) => i === idx ? { ...it, productId: '', name: '', barcode: '' } : it));
  }

  // إضافة منتج موجود إلى الأصناف — مشتركة بين المسح والبحث (الأعلى وداخل الصف).
  function addProduct(p, idx = null) {
    if (!p?._id) return;
    if (warehouseId && String(p.warehouseId?._id || p.warehouseId) !== String(warehouseId)) {
      toast.warning(`المنتج "${p.name}" لا ينتمي للمخزن المختار`);
    }
    const exists = items.findIndex((it) => String(it.productId) === String(p._id));
    if (exists >= 0 && idx == null) {
      setField(exists, 'qty', (Number(items[exists].qty) || 0) + 1);
      return;
    }
    // اختيار منتج مكرّر في صف آخر ليس خطأً (كميات بكلفتين مختلفتين) لكنه
    // يُنبَّه عليه حتى لا يُضاعَف صنف بالخطأ.
    if (idx != null && exists >= 0 && exists !== idx) {
      toast.warning(`المنتج "${p.name}" موجود أيضاً في الصف ${exists + 1}`);
    }
    // أسعار الفاتورة تسبق أسعار المنتج: ما كتبه المستخدم في الصف من كلفة أو
    // سعر بيع أو كمية يبقى كما هو عند ربط الصف بمنتج (مسح الباركود مثلاً)،
    // ولا يُستبدل بأسعار المنتج الافتراضية.
    const prev = idx != null ? items[idx] : null;
    const typedCost = prev && Number(prev.cost) > 0 ? Math.round(Number(prev.cost)) : null;
    const typedSale = prev && Number(prev.salePrice) > 0 ? Math.round(Number(prev.salePrice)) : null;
    const typedQty = prev && Number(prev.qty) > 1 ? Math.round(Number(prev.qty)) : 1;
    const newItem = {
      _key: (prev && prev._key) || (Date.now() + Math.random()),
      productId: p._id,
      name: p.name,
      barcode: p.barcode || '',
      qty: typedQty,
      cost: typedCost != null ? typedCost : (p.costPrice || 0),
      salePrice: typedSale != null ? typedSale : (p.salePrice || 0),
      // خصم الصف يُحترم كما كتبه المستخدم عند ربط الصف بمنتج.
      discount: prev ? (Number(prev.discount) || 0) : 0,
      discountType: prev && prev.discountType === 'percent' ? 'percent' : 'amount'
    };
    if (idx != null) {
      onChange(items.map((it, i) => i === idx ? newItem : it));
    } else {
      onChange([...items, newItem]);
    }
  }

  async function lookupBarcode(barcode, idx) {
    if (!barcode?.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/catalog/products/by-barcode/${encodeURIComponent(barcode.trim())}`);
      const p = data?.product || data?.data || data;
      if (!p?._id) {
        toast.error('لم يُعثر على المنتج');
        return;
      }
      addProduct(p, idx);
      setScanInput('');
    } catch (err) {
      if (err?.response?.status === 404) toast.error('الباركود غير معروف');
      else toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  }

  // البحث الأعلى: يضيف صنفاً جديداً إلى الجدول (أو يزيد كميته إن كان موجوداً).
  function pickResult(p) {
    addProduct(p);
    setSearchTerm('');
  }

  return (
    <div className="card">
      <div className="card-h">
        <div className="flex-1">
          <h3 className="text-[14.5px] font-bold">الأصناف</h3>
          <div className="text-xs text-ink-3 mt-0.5">{items.length} صنف</div>
        </div>
        {!readOnly ? (
          <>
            <ProductSearchBox
              value={searchTerm}
              onChange={setSearchTerm}
              onPick={pickResult}
              warehouseId={warehouseId}
              placeholder="بحث بالاسم أو SKU..."
              inputStyle={{ width: 220 }}
              disabled={busy}
            />
            <div className="relative">
              <input
                className="input pr-8 text-[12.5px]"
                placeholder="مسح باركود..."
                value={scanInput}
                dir="ltr"
                style={{ textAlign: 'right', fontFamily: 'monospace', width: 200 }}
                onChange={(e) => setScanInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    lookupBarcode(scanInput);
                  }
                }}
                disabled={busy}
              />
              <Icon name="scan" size={14} className="absolute top-1/2 -translate-y-1/2 right-2 text-ink-3 pointer-events-none" />
            </div>
            <button type="button" className="btn btn-primary" onClick={addRow}>
              <Icon name="plus" size={14} /> صف جديد
            </button>
          </>
        ) : null}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="text-ink-2 text-[12px]">
            <tr className="border-b border-line">
              <th className="p-2 text-start" style={{ width: 30 }}>#</th>
              <th className="p-2 text-start">المنتج / الباركود</th>
              <th className="p-2 text-start" style={{ width: 75 }}>الكمية</th>
              <th className="p-2 text-start" style={{ width: 110 }}>سعر الشراء للوحدة</th>
              <th className="p-2 text-start" style={{ width: 95 }}>خصم المنتج</th>
              <th className="p-2 text-start" style={{ width: 95 }}>نوع الخصم</th>
              <th className="p-2 text-start" style={{ width: 105 }}>الإجمالي قبل الخصم</th>
              <th className="p-2 text-start" style={{ width: 95 }}>قيمة الخصم</th>
              <th className="p-2 text-start" style={{ width: 110 }}>الإجمالي بعد الخصم</th>
              <th className="p-2 text-start" style={{ width: 70 }}>الهامش</th>
              <th className="p-2 text-start" style={{ width: 110 }}>سعر البيع</th>
              {!readOnly ? <th className="p-2" style={{ width: 30 }}></th> : null}
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => {
              const m = lineMath(it);
              return (
                <tr key={it._key || it.productId || idx} className="border-b border-line">
                  <td className="p-2 text-ink-3">{idx + 1}</td>
                  <td className="p-2">
                    {!readOnly && !it.productId ? (
                      // بحث داخل الصف نفسه: بالاسم أو SKU أو الباركود. اختيار
                      // النتيجة يملأ هذا الصف ولا يمسّ ما كُتب فيه من أرقام.
                      <div className="mb-1">
                        <ProductSearchBox
                          value={it.name || ''}
                          onChange={(v) => setField(idx, 'name', v)}
                          onPick={(p) => addProduct(p, idx)}
                          warehouseId={warehouseId}
                          placeholder="ابحث بالاسم أو الباركود..."
                          inputClassName="input text-[12.5px]"
                          inputStyle={{ padding: '6px 26px 6px 8px' }}
                          menuWidth={300}
                          floating
                          showEmpty
                          disabled={busy}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 mb-1">
                        <input
                          className="input text-[12.5px]"
                          style={{ padding: '6px 8px' }}
                          placeholder="اسم المنتج"
                          value={it.name || ''}
                          readOnly
                        />
                        {!readOnly ? (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            style={{ padding: '4px 6px' }}
                            onClick={() => unlinkRow(idx)}
                            title="تغيير المنتج (بحث جديد في هذا الصف)"
                          >
                            <Icon name="search" size={13} />
                          </button>
                        ) : null}
                      </div>
                    )}
                    <input
                      className="input text-[11px]"
                      style={{ padding: '4px 8px', fontFamily: 'monospace' }}
                      dir="ltr"
                      placeholder="الباركود"
                      value={it.barcode || ''}
                      readOnly={readOnly}
                      onChange={(e) => setField(idx, 'barcode', e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          lookupBarcode(it.barcode, idx);
                        }
                      }}
                    />
                  </td>
                  <td className="p-2">
                    <input
                      type="number"
                      min="1"
                      className="input text-[12.5px] text-center"
                      style={{ padding: '6px 8px' }}
                      value={it.qty}
                      readOnly={readOnly}
                      onChange={(e) => setField(idx, 'qty', e.target.value)}
                    />
                  </td>
                  <td className="p-2">
                    <input
                      type="number"
                      min="0"
                      className="input text-[12.5px]"
                      style={{ padding: '6px 8px' }}
                      value={it.cost}
                      readOnly={readOnly}
                      onChange={(e) => setField(idx, 'cost', e.target.value)}
                    />
                  </td>
                  <td className="p-2">
                    <input
                      type="number"
                      min="0"
                      step={it.discountType === 'percent' ? '0.5' : '1'}
                      className={clsx('input text-[12.5px]', m.invalid && 'ring-1 ring-red')}
                      style={{ padding: '6px 8px' }}
                      value={it.discount ?? 0}
                      readOnly={readOnly}
                      onChange={(e) => setField(idx, 'discount', e.target.value === '' ? 0 : Number(e.target.value))}
                    />
                    {m.invalid ? (
                      <div className="text-[10.5px] text-red font-semibold mt-0.5">
                        {m.type === 'percent' ? 'النسبة أكبر من 100%' : 'الخصم أكبر من إجمالي الصنف'}
                      </div>
                    ) : null}
                  </td>
                  <td className="p-2">
                    <select
                      className="input text-[12px]"
                      style={{ padding: '6px 4px' }}
                      value={it.discountType === 'percent' ? 'percent' : 'amount'}
                      disabled={readOnly}
                      onChange={(e) => setField(idx, 'discountType', e.target.value)}
                    >
                      <option value="amount">مبلغ ثابت</option>
                      <option value="percent">نسبة %</option>
                    </select>
                  </td>
                  <td className="p-2 text-ink-2">{formatIQD(m.grossTotal)}</td>
                  <td className="p-2" style={m.discountValue ? { color: '#B45309', fontWeight: 700 } : undefined}>
                    {m.discountValue ? formatIQD(m.discountValue) : '—'}
                  </td>
                  <td className="p-2 font-bold">{formatIQD(m.total)}</td>
                  <td className="p-2">
                    <Pill variant={marginColor(m.margin)}>{m.margin}%</Pill>
                  </td>
                  <td className="p-2">
                    <input
                      type="number"
                      min="0"
                      className="input text-[12.5px]"
                      style={{ padding: '6px 8px' }}
                      value={it.salePrice}
                      readOnly={readOnly}
                      onChange={(e) => setField(idx, 'salePrice', e.target.value)}
                    />
                  </td>
                  {!readOnly ? (
                    <td className="p-2">
                      <button
                        type="button"
                        className="text-red hover:opacity-80"
                        onClick={() => removeRow(idx)}
                        title="حذف الصف"
                      >
                        <Icon name="trash" size={14} />
                      </button>
                    </td>
                  ) : null}
                </tr>
              );
            })}
            {items.length === 0 ? (
              <tr>
                <td colSpan={readOnly ? 10 : 11} className="text-center text-ink-3 py-8 text-[12.5px]">
                  لا توجد أصناف. ابحث بالاسم أو امسح باركوداً أو أضف صفاً للبدء.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {!readOnly ? (
        <div className="px-4 py-2.5 bg-paper-2 border-t border-line text-[11.5px] text-ink-3">
          تلميح: ابحث داخل الصف نفسه بالاسم أو SKU أو الباركود واختر النتيجة لتُملأ بيانات الصف،
          أو استخدم البحث في الأعلى لإضافة صنف جديد، أو امسح الباركود لإضافته تلقائياً.
          <br />
          «خصم المنتج» مستقل لكل صف: اختر «مبلغ ثابت» أو «نسبة %». الهامش وكلفة المخزون
          يُحسبان على الكلفة بعد الخصم (الإجمالي بعد الخصم ÷ الكمية)، وخصم الفاتورة يُطبَّق فوقها.
        </div>
      ) : null}
    </div>
  );
}
